import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { compressImage } from "@/lib/offline-queue";
import { ARTIFACT_DEFS } from "@/components/teach-from-anything/constants";
import SourcePanel from "@/components/teach-from-anything/SourcePanel";
import { SettingsPanel, ArtifactPicker } from "@/components/teach-from-anything/Panels";
import { TFAEmpty, TFARunningSkeleton, TFAResult } from "@/components/teach-from-anything/ResultViews";

/* "Teach From Anything" — flagship one-shot workflow page. */

export default function TeachFromAnything() {
  const navigate = useNavigate();

  // Source state
  const [mode, setMode] = useState("upload"); // "upload" | "topic"
  const [preview, setPreview] = useState(null);
  const [base64, setBase64] = useState(null);
  const [mime, setMime] = useState("image/jpeg");
  const [docId, setDocId] = useState(null);
  const [docTitle, setDocTitle] = useState("");
  const [topic, setTopic] = useState("");

  // Settings
  const [subject, setSubject] = useState("Science");
  const [grade, setGrade] = useState("8");
  const [curriculum, setCurriculum] = useState("CBSE");
  const [language, setLanguage] = useState("English");
  const [targetLang, setTargetLang] = useState("Hindi");
  const [simplifyGrade, setSimplifyGrade] = useState("5");
  const [selected, setSelected] = useState(new Set(ARTIFACT_DEFS.map((a) => a.key)));
  const [saveLesson, setSaveLesson] = useState(true);

  // Run state
  const [extracting, setExtracting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const c = await compressImage(file, 1600, 0.78);
      setPreview(c.dataUrl);
      setMime(c.mime);
      const idx = c.dataUrl.indexOf(",");
      setBase64(idx >= 0 ? c.dataUrl.slice(idx + 1) : c.dataUrl);
      if (!docTitle) setDocTitle(file.name.replace(/\.[^.]+$/, ""));
      setDocId(null); // will need re-extract
    } catch {
      toast.error("Could not load image");
    }
  };

  const extractDocument = async () => {
    if (!base64) {
      toast.error("Please upload an image first");
      return null;
    }
    setExtracting(true);
    try {
      const { data } = await api.post("/ocr/extract", {
        image_base64: base64,
        mime_type: mime,
        title: docTitle || null,
      });
      setDocId(data.document_id);
      toast.success("Document ready");
      return data.document_id;
    } catch {
      toast.error("Could not read document");
      return null;
    } finally {
      setExtracting(false);
    }
  };

  const runWorkflow = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one artifact to generate");
      return;
    }

    let useDocId = docId;
    if (mode === "upload") {
      if (!useDocId) useDocId = await extractDocument();
      if (!useDocId) return;
    } else if (!topic.trim()) {
      toast.error("Type a topic to teach from");
      return;
    }

    setGenerating(true);
    setResult(null);
    setActiveTab(null);
    try {
      const { data } = await api.post("/workflows/teach-from-anything", {
        document_id: mode === "upload" ? useDocId : null,
        topic: mode === "topic" ? topic.trim() : null,
        subject,
        grade,
        curriculum,
        language,
        target_language: targetLang,
        target_grade_for_simplify: simplifyGrade,
        artifacts: Array.from(selected),
        save_as_lesson: saveLesson,
        lesson_title: mode === "upload" ? docTitle : topic.trim(),
      });
      setResult(data);
      const firstReady = data.artifacts.find((a) => !a.error);
      if (firstReady) setActiveTab(firstReady.kind);
      toast.success(`Generated ${data.artifacts.filter((a) => !a.error).length} artifacts in ${Math.round(data.duration_ms / 1000)}s`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Workflow failed");
    } finally {
      setGenerating(false);
    }
  };

  const copyActive = async () => {
    if (!result || !activeTab) return;
    const a = result.artifacts.find((x) => x.kind === activeTab);
    if (!a) return;
    try { await navigator.clipboard.writeText(a.content); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <Layout title="Teach from anything">
      {/* ===== Hero ===== */}
      <section className="tb-card-feature relative overflow-hidden mb-6" data-testid="tfa-hero">
        <div className="absolute inset-0 tb-grid-bg opacity-50 pointer-events-none" />
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
              <Sparkles className="h-3 w-3" /> Flagship workflow
            </span>
            <h1 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium leading-tight">
              Upload anything. <span className="text-muted-foreground">Teach everything.</span>
            </h1>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl">
              A textbook page, a whiteboard photo, or just a topic — TeeBee turns it into a lesson plan, quiz, worksheet, flashcards, presentation, homework, simplified version and translation in one shot.
            </p>
          </div>
          <Button
            variant="ghost"
            className="rounded-full self-start sm:self-auto"
            onClick={() => navigate("/dashboard")}
          >
            Back to dashboard
          </Button>
        </div>
      </section>

      {/* ===== Grid ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Left: Source + Settings */}
        <aside className="lg:col-span-5 space-y-4">
          <SourcePanel
            mode={mode} setMode={setMode}
            preview={preview}
            onPickFile={handleFile}
            docTitle={docTitle} setDocTitle={setDocTitle}
            topic={topic} setTopic={setTopic}
          />
          <SettingsPanel
            subject={subject} setSubject={setSubject}
            grade={grade} setGrade={setGrade}
            curriculum={curriculum} setCurriculum={setCurriculum}
            language={language} setLanguage={setLanguage}
            targetLang={targetLang} setTargetLang={setTargetLang}
            simplifyGrade={simplifyGrade} setSimplifyGrade={setSimplifyGrade}
            saveLesson={saveLesson} setSaveLesson={setSaveLesson}
          />
          <ArtifactPicker selected={selected} setSelected={setSelected} />

          <Button
            onClick={runWorkflow}
            disabled={generating || extracting}
            className="w-full h-12 rounded-full text-sm"
            data-testid="tfa-run"
          >
            {(generating || extracting) ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {extracting ? "Reading document…" : "Generating everything…"}</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Generate {selected.size} {selected.size === 1 ? "artifact" : "artifacts"}</>
            )}
          </Button>
        </aside>

        {/* Right: Result canvas */}
        <section className="lg:col-span-7 space-y-4">
          {!result && !generating && !extracting && <TFAEmpty />}
          {(generating || extracting) && (
            <TFARunningSkeleton
              extracting={extracting}
              selectedCount={selected.size}
              selectedKeys={generating ? Array.from(selected) : []}
            />
          )}
          {result && (
            <TFAResult
              result={result}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              onCopy={copyActive}
            />
          )}
        </section>
      </div>
    </Layout>
  );
}
